import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { AppState, Platform, type AppStateStatus } from 'react-native';

import { logger } from '@/core/logger';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { refreshAllContentful } from '@/core/services/contentfulRefreshService';

/**
 * Collapses the burst of AppState events one return can fire (e.g. iOS
 * inactive → active → inactive → active). Kept short so a quick
 * background → foreground still re-checks Contentful.
 */
const FOREGROUND_REFRESH_DEBOUNCE_MS = 3 * 1000;

/**
 * Native, app-wide: whenever the app comes back to the foreground, re-check
 * all Contentful data (see refreshAllContentful) so CMS changes show without
 * an app restart. Mounted once in the root layout, inside QueryProvider.
 * Renders nothing.
 */
export function ContentfulForegroundRefresh(): null {
  const queryClient = useQueryClient();
  const { currentLanguage, isLoading } = useI18nContext();
  const currentLanguageRef = useRef(currentLanguage);
  currentLanguageRef.current = currentLanguage;

  useEffect(() => {
    if (Platform.OS === 'web' || isLoading) {
      return undefined;
    }
    let previousState: AppStateStatus = AppState.currentState;
    let lastRefreshAt = 0;
    // One refresh at a time: on a slow network a second return to the app must
    // not start an overlapping fetch that races the first one's cache writes.
    let isRefreshing = false;
    const subscription = AppState.addEventListener('change', (nextState) => {
      const cameToForeground = previousState !== 'active' && nextState === 'active';
      previousState = nextState;
      if (
        !cameToForeground ||
        isRefreshing ||
        Date.now() - lastRefreshAt < FOREGROUND_REFRESH_DEBOUNCE_MS
      ) {
        return;
      }
      lastRefreshAt = Date.now();
      isRefreshing = true;
      refreshAllContentful(currentLanguageRef.current, queryClient)
        .catch((error) => {
          logger.warn('ContentfulForegroundRefresh', 'Foreground Contentful refresh failed', error);
        })
        .finally(() => {
          isRefreshing = false;
        });
    });
    return () => subscription.remove();
  }, [isLoading, queryClient]);

  return null;
}

export default ContentfulForegroundRefresh;
